import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Small } from 'foundations-components/transitional/components/Small';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Link } from 'foundations-components/transitional/components/Link';
import { useHasPreferredDisplayCurrencyScope } from '../../multiCurrency/hooks/useHasPreferredDisplayCurrency';
import { useCallback } from 'react';
import { useFetchPreferredCurrency } from '../../multiCurrency/hooks/useFetchPreferredCurrency';
import { isCurrencyCode } from '../../multiCurrency/utils/isCurrencyCode';
import { useLogger } from 'crm-object-board-core/logger';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { useMarkAndMeasureOnMount } from '../../performance/hooks/useMarkAndMeasureOnMount';
import { BOARD_FOOTER_MOUNTED_MEASURE } from '../../performance/measurements';
import { useOpenBoardSettingsPanel } from '../../boardSettingsPanel/useOpenBoardSettingsPanel';
import { useBoardSettings } from 'crm-object-board-core/frameworkAppSettings';
import { ORDER_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { OrderAmountTooltipText } from '../aggregators/amount/OrderAmountTooltipText';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import styled from 'styled-components';
import { useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SummaryLine = styled.span.withConfig({
  displayName: "AggregationFooter__SummaryLine",
  componentId: "sc-1aex49h-0"
})(["display:inline-block;overflow-x:hidden;text-overflow:ellipsis;white-space:nowrap;inline-size:100%;"]);
const LockedPreferredCurrencyLink = () => {
  const {
    openUpgradeModal
  } = useUpgradeModal('crm-pipelines-deal-preferred-currency-footer');
  return /*#__PURE__*/_jsxs(Text, {
    as: "div",
    typography: "body-100",
    marginBlockStart: "400",
    children: [/*#__PURE__*/_jsx(Link, {
      onClick: openUpgradeModal,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "crmObjectBoard.deals.displayInPreferredCurrency"
      })
    }), /*#__PURE__*/_jsx(LockBadge, {})]
  });
};
const PreferredCurrencyLink = () => {
  const logger = useLogger();
  const {
    preferredCurrency
  } = useFetchPreferredCurrency();
  const hasPreferredCurrencyScope = useHasPreferredDisplayCurrencyScope();
  const openBoardPreferences = useOpenBoardSettingsPanel();
  const boardSettings = useBoardSettings();
  const handleOpenEditCardsPanel = useCallback(() => {
    openBoardPreferences('BOARD_TAB_ID');
    logger.log({
      key: 'openBoardPreferences',
      metadata: {
        action: `open board preferences from footer tooltip`
      }
    });
  }, [openBoardPreferences, logger]);
  const hasBoardSettingsMultiCurrency = useMemo(() => {
    var _boardSettings$boardS;
    return Boolean(boardSettings === null || boardSettings === void 0 || (_boardSettings$boardS = boardSettings.boardSettingsMultiCurrency) === null || _boardSettings$boardS === void 0 ? void 0 : _boardSettings$boardS.value);
  }, [boardSettings]);
  if (isCurrencyCode(preferredCurrency) || !hasBoardSettingsMultiCurrency) {
    return null;
  }
  if (!hasPreferredCurrencyScope) {
    return /*#__PURE__*/_jsx(LockedPreferredCurrencyLink, {});
  }
  return /*#__PURE__*/_jsx(Text, {
    as: "div",
    typography: "body-100",
    marginBlockStart: "400",
    children: /*#__PURE__*/_jsx(Link, {
      onClick: handleOpenEditCardsPanel,
      "data-test-id": "aggregation-footer-change-preferred-currency-link",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "crmObjectBoard.deals.displayInPreferredCurrency"
      })
    })
  });
};
const SummaryTooltipText = props => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h7",
      marginBlockEnd: "200",
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "crmObjectBoard.deals.companyCurrency"
      })
    }), props.tooltipTexts.map((text, index) => {
      return /*#__PURE__*/_jsx(TFlex, {
        direction: "row",
        align: "center",
        justify: "between",
        "data-test-id": `summary-tooltip-line-${index + 1}`,
        children: text
      }, index);
    }), /*#__PURE__*/_jsx(PreferredCurrencyLink, {})]
  });
};
const SummaryText = props => {
  const hasBoardCardV2 = useHasCardV2();
  return /*#__PURE__*/_jsx(TFlex, {
    direction: "column",
    align: hasBoardCardV2 ? 'start' : 'center',
    justify: "center",
    "data-test-id": "summary-tooltip-hover-trigger",
    "data-selenium-test": !props.texts.length ? 'board-aggregation-loading' : 'board-aggregation-loaded',
    gap: hasBoardCardV2 ? 4 : 0,
    width: "100%",
    children: props.texts.map((text, index) => {
      if (index === 0 || hasBoardCardV2) {
        return /*#__PURE__*/_jsx(SummaryLine, {
          "data-test-id": `summary-line-${index + 1}`,
          children: text
        }, index);
      }
      return /*#__PURE__*/_jsx(Small, {
        "data-test-id": `summary-line-${index + 1}`,
        children: text
      }, index);
    })
  });
};
export const AggregationFooter = ({
  texts,
  tooltipTexts,
  columnData
}) => {
  useMarkAndMeasureOnMount(BOARD_FOOTER_MOUNTED_MEASURE);
  const objectTypeId = useObjectTypeId();
  const isOrderObject = objectTypeId === ORDER_TYPE_ID;
  if (isOrderObject) {
    return /*#__PURE__*/_jsx(Tooltip, {
      "data-test-id": "summary-tootlip",
      title: /*#__PURE__*/_jsx(OrderAmountTooltipText, {}),
      children: /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(SummaryText, {
          texts: texts
        })
      })
    });
  }
  if (tooltipTexts.length) {
    return /*#__PURE__*/_jsx(Tooltip, {
      maxInlineSize: "500px",
      "data-test-id": "summary-tootlip",
      use: "longform",
      headingText: columnData.name,
      title: /*#__PURE__*/_jsx(SummaryTooltipText, {
        tooltipTexts: tooltipTexts
      }),
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        as: "span",
        children: /*#__PURE__*/_jsx(SummaryText, {
          texts: texts
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(SummaryText, {
    texts: texts
  });
};
export default AggregationFooter;